import { useState, useEffect, useRef, useCallback } from 'react';
import { Button, Select, TextInput } from "flowbite-react";
import { HiSearch, HiX, HiArrowUp, HiArrowDown } from 'react-icons/hi';

function SearchPanel({ tasks, onHighlightChange }) {
    // Search state
    const [searchTerm, setSearchTerm] = useState('');
    const [searchField, setSearchField] = useState('all');
    const [searchResults, setSearchResults] = useState([]);
    const [currentHighlightIndex, setCurrentHighlightIndex] = useState(-1);
    const [isSearchPanelOpen, setIsSearchPanelOpen] = useState(false);
    const searchInputRef = useRef(null);

    // Navigate through search results - memoized with useCallback
    const navigateSearchResults = useCallback((direction) => {
        if (searchResults.length === 0) return;
        
        let newIndex;
        if (direction === 'next') {
            newIndex = currentHighlightIndex + 1 >= searchResults.length ? 0 : currentHighlightIndex + 1;
        } else {
            newIndex = currentHighlightIndex - 1 < 0 ? searchResults.length - 1 : currentHighlightIndex - 1;
        }
        
        setCurrentHighlightIndex(newIndex);
        
        // Notify parent about the newly highlighted task
        if (searchResults[newIndex]) {
            onHighlightChange(searchResults[newIndex].id);
            
            // Scroll to highlighted task - use a safe approach
            try {
                const taskElement = document.getElementById(searchResults[newIndex].id);
                if (taskElement) {
                    setTimeout(() => {
                        taskElement.scrollIntoView({
                            behavior: 'smooth',
                            block: 'center'
                        });
                    }, 100);
                }
            } catch (error) {
                console.warn('Error scrolling to task:', error);
            }
        }
    }, [searchResults, currentHighlightIndex, onHighlightChange]);

    const searchOptions = [
        { value: "all", label: "All Fields" },
        { value: "id", label: "Task ID" },
        { value: "name", label: "Task Name" },
        { value: "member", label: "Assigned Member" },
        { value: "progress", label: "Progress" }
    ];

    // Add keyboard shortcut for search
    useEffect(() => {
        const handleKeyDown = (e) => {
            // Check for Ctrl+F or Cmd+F (for Mac)
            if ((e.ctrlKey || e.metaKey) && e.key === 'f') {
                e.preventDefault(); // Prevent browser's default search
                setIsSearchPanelOpen(true);
                
                // Focus the search input
                setTimeout(() => {
                    if (searchInputRef.current) {
                        const inputElement = searchInputRef.current.querySelector('input');
                        if (inputElement) inputElement.focus();
                    }
                }, 100);
            }
            
            // Navigation with arrow keys when search panel is open
            if (isSearchPanelOpen && searchResults.length > 0) {
                // Up arrow for previous result (support both standard and legacy key names)
                if (e.key === 'ArrowUp' || e.key === 'Up') {
                    e.preventDefault();
                    e.stopPropagation();
                    navigateSearchResults('prev');
                    return false;
                }
                // Down arrow for next result (support both standard and legacy key names)
                else if (e.key === 'ArrowDown' || e.key === 'Down') {
                    e.preventDefault();
                    e.stopPropagation();
                    navigateSearchResults('next');
                    return false;
                }
            }
        };
        
        // Add event listener with capture phase to ensure it gets priority
        document.addEventListener('keydown', handleKeyDown, true);
        
        // Cleanup function
        return () => {
            document.removeEventListener('keydown', handleKeyDown, true);
        };
    }, [isSearchPanelOpen, searchResults.length, navigateSearchResults]);

    // Handle search
    useEffect(() => {
        if (!searchTerm.trim() || !tasks || tasks.length === 0) {
            setSearchResults([]);
            setCurrentHighlightIndex(-1);
            // Notify parent that nothing is highlighted
            onHighlightChange(null);
            return;
        }

        const term = searchTerm.toLowerCase();
        // Skip the project task (index 0) in the search results
        const matches = tasks.slice(1).filter(task => {
            if (searchField === "all") {
                return (
                    (task.name && task.name.toLowerCase().includes(term)) || 
                    (task.id && task.id.toLowerCase().includes(term)) ||
                    (task.member?.name && task.member.name.toLowerCase().includes(term)) ||
                    (task.progress && task.progress.toString().includes(term))
                );
            }
            
            switch (searchField) {
                case "id":
                    return task.id && task.id.toLowerCase().includes(term);
                case "name":
                    return task.name && task.name.toLowerCase().includes(term);
                case "member":
                    return task.member?.name && task.member.name.toLowerCase().includes(term);
                case "progress":
                    // Convert search term to number for exact progress matching
                    const searchNum = parseInt(term);
                    if (!isNaN(searchNum)) {
                        // Exact match for progress percentage
                        return task.progress === searchNum;
                    }
                    // If not a number, do string comparison
                    return task.progress && task.progress.toString().includes(term);
                default:
                    return false;
            }
        });
        
        setSearchResults(matches);
        
        // Set first result as highlighted and notify parent
        if (matches.length > 0) {
            setCurrentHighlightIndex(0);
            onHighlightChange(matches[0].id);
        } else {
            setCurrentHighlightIndex(-1);
            onHighlightChange(null);
        }
    }, [searchTerm, searchField, tasks, onHighlightChange]);

    // Handle search term changes
    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchTerm(value);
    };
    
    // Handle search field changes
    const handleFieldChange = (e) => {
        const newField = e.target.value;
        setSearchField(newField);
        setSearchTerm(''); // Clear search when changing field
        
        // Re-focus on the input
        setTimeout(() => {
            if (searchInputRef.current) {
                const inputElement = searchInputRef.current.querySelector('input');
                if (inputElement) inputElement.focus();
            }
        }, 0);
    };
    
    // Handle clearing the search
    const handleClearSearch = () => {
        setSearchTerm('');
        
        // Re-focus on the input
        setTimeout(() => {
            if (searchInputRef.current) {
                const inputElement = searchInputRef.current.querySelector('input');
                if (inputElement) inputElement.focus();
            }
        }, 0);
    };

    // Toggle search panel
    const toggleSearchPanel = () => {
        const newState = !isSearchPanelOpen;
        setIsSearchPanelOpen(newState);
        
        // Clear fields when closing the panel
        if (!newState) {
            setSearchTerm('');
            setSearchResults([]);
            setCurrentHighlightIndex(-1);
            setSearchField('all'); // Reset to default
            onHighlightChange(null); // Clear highlighting
        }
        
        // Focus on search input when opened
        if (newState) {
            setTimeout(() => {
                if (searchInputRef.current) {
                    const inputElement = searchInputRef.current.querySelector('input');
                    if (inputElement) inputElement.focus();
                }
            }, 100);
        }
    };

    return (
        <>
            {/* Floating Search Button - repositioned and styled for better alignment with export */}
            {!isSearchPanelOpen && (
                <button 
                    type="button"
                    className="absolute top-[82px] right-28 z-50 rounded-md bg-blue-600 text-white 
                    font-medium px-3 py-2 shadow-sm hover:bg-blue-700 dark:bg-blue-700 
                    dark:hover:bg-blue-800 flex items-center gap-2 outline-none focus:ring-2 
                    focus:ring-blue-400 focus:ring-opacity-50"
                    onClick={toggleSearchPanel}
                >
                    <HiSearch className="w-4 h-4" /> 
                    <span className="font-['Inter'] font-medium text-sm">Search</span>
                </button>
            )}
            
            {/* Floating Search Panel */}
            {isSearchPanelOpen && (
                <div className="absolute top-[90px] right-5 z-[999] w-full max-w-[700px] shadow-lg border border-gray-300 dark:border-gray-600 rounded-lg animate-[fadeIn_0.3s_ease-out]">
                    <div className="flex flex-row gap-2 p-3 bg-gray-100 dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg">
                        {/* Search Field Selector */}
                        <div className="w-36">
                            <Select
                                id="search-field"
                                value={searchField}
                                onChange={handleFieldChange}
                                className="w-full"
                                size="sm"
                            >
                                {searchOptions.map((option) => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </Select>
                        </div>

                        {/* Search Bar */}
                        <div className="flex-1 relative" ref={searchInputRef}>
                            <TextInput
                                id="gantt-search"
                                type="text"
                                icon={HiSearch}
                                placeholder={`Search by ${searchOptions.find(opt => opt.value === searchField)?.label.toLowerCase()}...`}
                                value={searchTerm}
                                onChange={handleSearchChange}
                                className="w-full"
                                sizing="sm"
                            />
                            {searchTerm && (
                                <Button 
                                    size="xs" 
                                    color="light"
                                    className="absolute right-2 top-1/2 transform -translate-y-1/2"
                                    onClick={handleClearSearch}
                                >
                                    <HiX />
                                </Button>
                            )}
                        </div>
                        
                        {/* Search Result Navigation and Close Button in same row */}
                        <div className="flex items-center gap-2 ml-auto">
                            {searchResults.length > 0 && (
                                <>
                                    <span className="text-xs bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 px-2 py-1 rounded-full">
                                        {currentHighlightIndex + 1} of {searchResults.length}
                                    </span>
                                    <Button color="light" size="xs" onClick={() => navigateSearchResults('prev')}>
                                        <HiArrowUp />
                                    </Button>
                                    <Button color="light" size="xs" onClick={() => navigateSearchResults('next')}>
                                        <HiArrowDown />
                                    </Button>
                                </>
                            )}
                            
                            {/* Minimize Button */}
                            <Button 
                                color="light" 
                                size="xs"
                                className="self-center h-7 w-7 flex items-center justify-center"
                                onClick={() => {
                                    setIsSearchPanelOpen(false);
                                    setSearchTerm('');
                                    setSearchResults([]);
                                    setCurrentHighlightIndex(-1);
                                    setSearchField('all');
                                    onHighlightChange(null); // Clear highlighting
                                }}
                                title="Minimize search panel"
                            >
                                <HiX />
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

export default SearchPanel; 